/**
 * useFormResponses — read/write the case-level key/value store for fields
 * that don't fit the relational data model (A-number, SSN, oath checkboxes,
 * naturalization basis, etc.).
 *
 * The Go backend exposes this as PUT /v1/cases/{cid}/form-responses/{type}/{edition}/{key}
 * and persists `value` as a JSONB column. We default to N-400 / 2025-01-20.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formResponses } from "@/lib/endpoints";
import type { FormResponsesListResponse } from "@/lib/api-types";

export function useFormResponses(caseId: string | undefined) {
  const qc = useQueryClient();
  const key = ["case", caseId, "form-responses"] as const;

  const list = useQuery({
    queryKey: key,
    queryFn: () => formResponses.list(caseId!),
    enabled: !!caseId,
  });

  // Indexed by field_key for ergonomic lookups.
  const byKey = new Map<string, unknown>();
  for (const r of list.data?.items ?? []) {
    byKey.set(r.field_key, r.value);
  }

  /** Returns the stored value (parsed) or undefined. */
  const get = <T = unknown>(fieldKey: string): T | undefined =>
    byKey.get(fieldKey) as T | undefined;

  /** Returns string value or "" — handy for controlled inputs. */
  const getString = (fieldKey: string): string => {
    const v = byKey.get(fieldKey);
    return typeof v === "string" ? v : "";
  };

  const upsert = useMutation({
    mutationFn: ({ fieldKey, value }: { fieldKey: string; value: unknown }) =>
      formResponses.upsert(caseId!, fieldKey, value),
    onSuccess: (resp) => {
      qc.setQueryData<FormResponsesListResponse>(key, (prev) => {
        if (!prev) return { items: [resp], total: 1 };
        const idx = prev.items.findIndex((r) => r.field_key === resp.field_key);
        if (idx === -1) {
          return { items: [...prev.items, resp], total: prev.total + 1 };
        }
        const items = [...prev.items];
        items[idx] = resp;
        return { ...prev, items };
      });
      qc.invalidateQueries({ queryKey: ["case", caseId, "evaluation"] });
      // packet-status compares the current snapshot hash against the
      // last rendered hash; any form_response change can flip it from
      // "current" to "outdated", so refresh it after every upsert.
      qc.invalidateQueries({ queryKey: ["case", caseId, "packet-status"] });
    },
  });

  return {
    isLoading: list.isLoading,
    get,
    getString,
    upsert,
  };
}
